// src/components/layout/MagicDashboardLayout.jsx
import React, { useState, useEffect, useMemo, Suspense } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Tooltip,
  useMediaQuery,
  useTheme,
  alpha,
  styled,
  Collapse
} from '@mui/material';
import { PERMISSION_RESOURCES, PERMISSION_ACTIONS } from '../../constants/permissionConstants';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Campaign as CampaignIcon,
  Article as ArticleIcon,
  Assignment as AssignmentIcon,
  CalendarMonth as CalendarIcon,
  ListAlt as ListAltIcon,
  BarChart as BarChartIcon,
  Receipt as ReceiptIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  Apps as AppsIcon,
  AutoAwesome as MagicIcon,
  Psychology as SOSTACIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AccountTree as ProcessFlowIcon,
  LibraryBooks as LibraryBooksIcon,
  Description as FormIcon,
  Edit as FormBuilderIcon,
  AccountBalance as AccountBalanceIcon,
  Payment as PaymentIcon,
  Calculate as CalculateIcon,
  RequestQuote as QuoteIcon,
  Groups as GroupsIcon,
  EventAvailable as EventAvailableIcon,
  Assessment as AssessmentIcon,
  PlaylistPlay as PlaylistIcon,
  Description as DescriptionIcon,
  Analytics as AnalyticsIcon,
  CheckCircle as ConnectedIcon,
  Error as DisconnectedIcon,
  PictureAsPdf as PDFIcon,
  Add as AddIcon,
  Folder as FolderIcon,
  Timer as TimerIcon
} from '@mui/icons-material';
import { keyframes } from '@mui/system';

// Import stores for state management
import useAuthStore from '../../stores/authStore';
import useSettingsStore from '../../stores/settingsStore';
import useUIStore from '../../stores/uiStore';
import useNotificationStore from '../../stores/notificationStore';
import notificationSoundService from '../../services/notificationSoundService';

// Import custom components
import MagicLoading from '../ui/MagicLoading';
import NotificationsMenu from '../features/notifications/NotificationsMenu';
import { KeyboardShortcuts, ContextMenu } from '../tasks/entry-points';
import { useSocketConnection, useSocketEvent } from '../../hooks/useSocketEvent';
import ProjectSwitcher from './ProjectSwitcher';
import useProjectSwitcher from '../../hooks/useProjectSwitcher';
import GlobalTaskDrawer from '../tasks/GlobalTaskDrawer';
import { OnlineUsersWidget } from '../OnlineUsers';

// Theme hooks
import { useThemeTokens, useGradientText, useMagicalEffect } from '../../theme/themeHooks';

// Constants
const DRAWER_WIDTH = 280;
const DRAWER_COLLAPSED_WIDTH = 72;

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideInLeft = keyframes`
  from {
    transform: translateX(-20px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const blink = keyframes`
  0%, 50% {
    opacity: 1;
  }
  51%, 100% {
    opacity: 0.4;
  }
`;

// Styled components
const StyledAppBar = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'collapsed',
})(({ theme, open, collapsed }) => ({
  // zIndex: theme.zIndex.drawer + 1,
  boxShadow: theme.aghrba?.colors?.shadows?.cardShadow(theme.palette.mode) || theme.shadows[4],
  transition: theme.aghrba?.transitions?.standard || theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  animation: `${fadeIn} 0.5s ease-in`,
  ...(open && {
    marginLeft: collapsed ? DRAWER_COLLAPSED_WIDTH : DRAWER_WIDTH,
    width: `calc(100% - ${collapsed ? DRAWER_COLLAPSED_WIDTH : DRAWER_WIDTH}px)`,
    transition: theme.aghrba?.transitions?.standard || theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'collapsed',
})(({ theme, open, collapsed }) => ({
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  '& .MuiDrawer-paper': {
    width: collapsed ? DRAWER_COLLAPSED_WIDTH : DRAWER_WIDTH,
    transition: theme.aghrba?.transitions?.standard || theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxShadow: theme.aghrba?.colors?.shadows?.cardShadow(theme.palette.mode),
    borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    display: 'flex',
    flexDirection: 'column',
    animation: `${fadeIn} 0.5s ease-in`,
    background: theme.aghrba?.gradients?.pageBackground,
    '&::-webkit-scrollbar': {
      width: 8,
    },
    '&::-webkit-scrollbar-track': {
      background: alpha(theme.palette.background.default, 0.1),
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: alpha(theme.palette.primary.main, 0.2),
      borderRadius: 4,
      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.3),
      },
    },
  },
  ...(collapsed && {
    [theme.breakpoints?.up('sm') || '@media (min-width: 600px)']: {
      '& .MuiDrawer-paper': {
        width: DRAWER_COLLAPSED_WIDTH,
      },
    },
  }),
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(0, 2),
  ...theme.mixins.toolbar,
  background: theme.aghrba?.gradients?.pageBackground,
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
}));

const Main = styled('main', {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'collapsed',
})(({ theme, open, collapsed }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.aghrba?.transitions?.standard || theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: 0,
  minHeight: '100vh',
  animation: `${fadeIn} 0.5s ease-in`,
  background: theme.aghrba?.gradients?.pageBackground,
  ...(open && {
    transition: theme.aghrba?.transitions?.standard || theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: collapsed ? DRAWER_COLLAPSED_WIDTH : DRAWER_WIDTH,
  }),
}));

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  borderRadius: theme.aghrba?.tokens?.borderRadius?.button || theme.shape.borderRadius * 2,
  margin: '4px 8px',
  transition: theme.aghrba?.transitions?.standard || 'all 0.3s ease',
  fontFamily: '"Poppins", sans-serif',
  ...(active && {
    background: theme.palette.mode === 'dark' 
      ? alpha(theme.palette.secondary.main, 0.15)
      : alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.mode === 'dark' 
      ? theme.palette.secondary.main
      : theme.palette.primary.main,
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '25%',
      height: '50%',
      width: 4,
      background: theme.aghrba?.gradients?.secondaryButton || theme.palette.secondary.main,
      borderRadius: '0 4px 4px 0',
      animation: theme.aghrba?.animations?.glow,
    },
  }),
  '&:hover': {
    backgroundColor: active 
      ? (theme.palette.mode === 'dark' 
        ? alpha(theme.palette.secondary.main, 0.2)
        : alpha(theme.palette.primary.main, 0.15))
      : alpha(theme.palette.primary.main, 0.05),
    transform: 'translateX(4px)',
  },
}));

const ListItemAnimation = styled('div')(({ delay = 0 }) => ({
  animation: `${slideInLeft} 0.5s ease forwards`,
  animationDelay: `${delay * 0.05}s`,
  opacity: 0,
}));

const Logo = styled('div')(({ theme, collapsed }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: collapsed ? theme.spacing(1) : theme.spacing(1, 2),
  cursor: 'pointer',
  transition: theme.aghrba?.transitions?.standard,
  '&:hover': {
    transform: 'translateY(-2px)',
  },
  '& svg, & img': {
    height: 32,
    width: 32,
    marginRight: collapsed ? 0 : theme.spacing(1),
    transition: theme.aghrba?.transitions?.standard,
    animation: `${fadeIn} 0.5s ease-in`,
  },
}));

const MagicalAvatar = styled(Avatar)(({ theme }) => ({
  width: 36,
  height: 36,
  background: theme.aghrba?.gradients?.secondaryButton,
  boxShadow: theme.aghrba?.effects?.cardGlow,
  transition: theme.aghrba?.transitions?.standard,
  animation: `${fadeIn} 0.5s ease-in`,
  fontFamily: '"Poppins", sans-serif',
  fontWeight: 600,
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.aghrba?.effects?.magicalButton,
  },
}));

const MagicalIconButton = styled(IconButton)(({ theme }) => ({
  transition: theme.aghrba?.transitions?.standard,
  borderRadius: '50%',
  position: 'relative',
  '&:hover': {
    transform: 'translateY(-2px)',
    background: alpha(theme.palette.background.default, 0.1),
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    transform: 'translate(-50%, -50%)',
    background: 'radial-gradient(circle at center, transparent 0%, rgba(228, 180, 76, 0.2) 70%)',
    opacity: 0,
    transition: theme.aghrba?.transitions?.standard,
  },
  '&:hover::before': {
    opacity: 1,
  },
}));

const MagicNotificationBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    animation: theme.aghrba?.animations?.glow,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: theme.aghrba?.animations?.glow,
      border: '1px solid currentColor',
      content: '""',
    },
  },
}));

// Permission-based navigation items
const navigationItems = [
  {
    text: 'Dashboard',
    icon: <DashboardIcon />,
    path: '/dashboard',
    permissionRequired: false
  },
  {
    text: 'Projects',
    icon: <ArticleIcon />,
    path: '/projects',
    permissionRequired: true,
    resource: 'projects',
    action: 'read'
  },
  {
    text: 'Project Templates',
    icon: <FolderIcon />,
    path: '/project-templates',
    permissionRequired: true,
    resource: 'project_templates',
    action: 'read'
  },
  {
    text: 'Tasks',
    icon: <AssignmentIcon />,
    path: '/tasks',
    permissionRequired: false
  },
  {
    text: 'Global Calendar',
    icon: <CalendarIcon />,
    path: '/calendar',
    permissionRequired: true,
    resource: PERMISSION_RESOURCES.GLOBAL_CALENDAR,
    action: PERMISSION_ACTIONS.READ
  },
  {
    text: 'Clients',
    icon: <BusinessIcon />,
    path: '/clients',
    permissionRequired: true,
    resource: 'clients',
    action: 'read'
  },
  {
    text: 'Brands',
    icon: <BusinessIcon />,
    path: '/brands',
    permissionRequired: true,
    resource: 'brands',
    action: 'read'
  },
  {
    text: 'Content',
    icon: <ArticleIcon />,
    path: '/content',
    permissionRequired: true,
    resource: 'content',
    action: 'read',
    children: [
      {
        text: 'Library',
        icon: <LibraryBooksIcon />,
        path: '/content',
        permissionRequired: true,
        resource: 'content',
        action: 'read'
      },
    ]
  },
  {
    text: 'SOSTAC',
    icon: <SOSTACIcon />,
    path: '/sostac',
    permissionRequired: true,
    resource: 'sostac',
    action: 'read'
  },
  {
    text: 'Process Flows',
    icon: <ProcessFlowIcon />,
    path: '/process-flows',
    permissionRequired: true,
    resource: PERMISSION_RESOURCES.PROCESS_FLOW,
    action: PERMISSION_ACTIONS.READ
  },
  {
    text: 'Forms',
    icon: <FormIcon />,
    path: '/forms',
    permissionRequired: true,
    resource: 'forms',
    action: 'read',
    children: [
      {
        text: 'All Forms',
        icon: <FormIcon />,
        path: '/forms',
        permissionRequired: true,
        resource: 'forms',
        action: 'read'
      },
      {
        text: 'Create Form',
        icon: <FormBuilderIcon />,
        path: '/forms/create',
        permissionRequired: true,
        resource: 'forms',
        action: 'create'
      },
    ]
  },
  {
    text: 'PDFs',
    icon: <PDFIcon />,
    path: '/pdf',
    permissionRequired: true,
    resource: 'pdfs',
    action: 'read',
    children: [
      {
        text: 'All PDFs',
        icon: <PDFIcon />,
        path: '/pdf',
        permissionRequired: true,
        resource: 'pdfs',
        action: 'read'
      },
      {
        text: 'Create PDF',
        icon: <AddIcon />,
        path: '/pdf/create',
        permissionRequired: true,
        resource: 'pdfs',
        action: 'create'
      },
      {
        text: 'Categories',
        icon: <FolderIcon />,
        path: '/pdf/categories',
        permissionRequired: true,
        resource: 'pdfs',
        action: 'manage_categories'
      },
    ]
  },
  {
    text: 'Campaigns',
    icon: <CampaignIcon />,
    path: '/campaigns',
    permissionRequired: true,
    resource: 'campaigns',
    action: 'read'
  },
  {
    text: 'Leads',
    icon: <PersonIcon />,
    path: '/leads',
    permissionRequired: true,
    resource: 'leads',
    action: 'read'
  },
  {
    text: 'Workflows',
    icon: <ListAltIcon />,
    path: '/workflows',
    permissionRequired: true,
    resource: PERMISSION_RESOURCES.WORKFLOW_AUTOMATION,
    action: PERMISSION_ACTIONS.READ
  },
  {
    text: 'HR',
    icon: <GroupsIcon />,
    path: '/hr',
    permissionRequired: false,
    children: [
      {
        text: 'Dashboard',
        icon: <DashboardIcon />,
        path: '/hr',
        permissionRequired: true,
        resource: 'employees',
        action: 'read'
      },
      {
        text: 'Employees',
        icon: <PeopleIcon />,
        path: '/hr/employees',
        permissionRequired: true,
        resource: 'employees',
        action: 'read'
      },
      {
        text: 'Leave Management',
        icon: <EventAvailableIcon />,
        path: '/hr/leave',
        permissionRequired: true,
        resource: 'leaves',
        action: 'read'
      },
      {
        text: 'Attendance',
        icon: <CalendarIcon />,
        path: '/hr/attendance',
        permissionRequired: true,
        resource: 'attendance',
        action: 'read'
      },
      {
        text: 'My Attendance',
        icon: <CalendarIcon />,
        path: '/hr/my-attendance',
        permissionRequired: false
      },
      {
        text: 'Performance',
        icon: <AssessmentIcon />,
        path: '/hr/performance',
        permissionRequired: true,
        resource: 'performance',
        action: 'read'
      },
      {
        text: 'Goals',
        icon: <MagicIcon />,
        path: '/hr/goals',
        permissionRequired: true,
        resource: 'goals',
        action: 'read'
      },
      {
        text: 'Recruitment',
        icon: <PersonIcon />,
        path: '/hr/recruitment',
        permissionRequired: true,
        resource: 'recruitment',
        action: 'read'
      },
      {
        text: 'Organization Chart',
        icon: <BusinessIcon />,
        path: '/hr/organization',
        permissionRequired: true,
        resource: 'organization',
        action: 'read'
      },
      {
        text: 'Payroll',
        icon: <PaymentIcon />,
        path: '/payroll',
        permissionRequired: true,
        resource: 'payroll',
        action: 'read',
        children: [
          {
            text: 'Dashboard',
            icon: <DashboardIcon />,
            path: '/payroll',
            permissionRequired: true,
            resource: 'payroll',
            action: 'read'
          },
          {
            text: 'Payroll Runs',
            icon: <PlaylistIcon />,
            path: '/payroll/runs',
            permissionRequired: true,
            resource: 'payroll',
            action: 'read'
          },
          {
            text: 'Payslips',
            icon: <DescriptionIcon />,
            path: '/payroll/payslips',
            permissionRequired: true,
            resource: 'payroll',
            action: 'read'
          },
          {
            text: 'Reports',
            icon: <AnalyticsIcon />,
            path: '/payroll/reports',
            permissionRequired: true,
            resource: 'payroll',
            action: 'read'
          },
          {
            text: 'Settings',
            icon: <SettingsIcon />,
            path: '/payroll/settings',
            permissionRequired: true,
            resource: 'payroll',
            action: 'update'
          }
        ]
      },
    ]
  },
  {
    text: 'Users',
    icon: <PeopleIcon />,
    path: '/users',
    permissionRequired: true,
    resource: 'users',
    action: 'delete',
  },
  {
    text: 'Admin',
    icon: <SettingsIcon />,
    path: '/admin',
    permissionRequired: true,
    resource: 'superadmin',
    action: 'manage',
    children: [
      {
        text: 'Roles',
        icon: <PeopleIcon />,
        path: '/admin/roles',
        permissionRequired: true,
        resource: 'superadmin',
        action: 'manage'
      },
    ]
  },
  {
    text: 'Finance',
    icon: <AccountBalanceIcon />,
    path: '/finance',
    permissionRequired: true,
    resource: 'finances',
    action: 'read',
    children: [
      {
        text: 'Invoices',
        icon: <ReceiptIcon />,
        path: '/invoices',
        permissionRequired: true,
        resource: 'finances',
        action: 'read'
      },
      {
        text: 'Expenses',
        icon: <PaymentIcon />,
        path: '/finances/expenses',
        permissionRequired: true,
        resource: 'expense',
        action: 'read'
      },
      {
        text: 'Vendors',
        icon: <BusinessIcon />,
        path: '/finances/vendors',
        permissionRequired: true,
        resource: 'vendors',
        action: 'read'
      },
      {
        text: 'Pricing Calculator',
        icon: <CalculateIcon />,
        path: '/pricing/calculator',
        permissionRequired: true,
        resource: 'quotes',
        action: 'create'
      },
      {
        text: 'Quotes',
        icon: <QuoteIcon />,
        path: '/pricing/quotes',
        permissionRequired: true,
        resource: 'quotes',
        action: 'read'
      }
    ]
  },
  {
    text: 'Analytics',
    icon: <BarChartIcon />,
    path: '/analytics',
    permissionRequired: true,
    resource: 'analytics',
    action: 'read'
  },
  {
    text: 'Time Tracking Analytics',
    icon: <TimerIcon />,
    path: '/time-tracking-analytics',
    permissionRequired: true,
    resource: 'time_tracking_analytics',
    action: 'read'
  },
  {
    text: 'Settings',
    icon: <SettingsIcon />,
    path: '/settings',
    permissionRequired: false,
    children: [
      {
        text: 'General Settings',
        icon: <SettingsIcon />,
        path: '/settings',
        permissionRequired: false
      },
      {
        text: 'Pricing Configuration',
        icon: <CalculateIcon />,
        path: '/settings/pricing',
        permissionRequired: true,
        resource: 'pricing_config',
        action: 'read'
      }
    ]
  },
];

// The MagicDashboardLayout component
const MagicDashboardLayout = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints?.down('md') || '(max-width: 960px)');
  
  // Theme tokens
  const { tokens, getCardStyle } = useThemeTokens();
  const { style: gradientTextStyle } = useGradientText();
  const floatEffect = useMagicalEffect('float');
  const glowEffect = useMagicalEffect('glow');
  
  // Get state from stores
  const { user, isAuthenticated, hasPermission, logout } = useAuthStore();
  const { settings, updateSetting } = useSettingsStore();
  
  // Socket connection status
  const isSocketConnected = useSocketConnection();
  const { 
    sidebarOpen, 
    sidebarCollapsed, 
    mobileDrawerOpen,
    toggleSidebar, 
    openSidebar, 
    closeSidebar,
    toggleSidebarCollapse,
    openMobileDrawer,
    closeMobileDrawer
  } = useUIStore();
  const { 
    unreadCount, 
    fetchUnreadCount,
    snackbar, 
    closeSnackbar 
  } = useNotificationStore();
  
  // Local state
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [notificationsAnchor, setNotificationsAnchor] = useState(null);
  const [pageLoading, setPageLoading] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  
  // Initialize project switcher hook for keyboard shortcuts
  useProjectSwitcher();
  
  // Fetch unread count on component mount and initialize notification sounds
  useEffect(() => {
    if (isAuthenticated) {
      fetchUnreadCount();
      // Initialize notification sound service
      notificationSoundService.init().then(() => {
        console.log('Notification sound service initialized');
      }).catch(err => {
        console.warn('Failed to initialize notification sounds:', err);
      });
    }
  }, [isAuthenticated, fetchUnreadCount]);
  
  // Listen for real-time notification updates
  useSocketEvent('notification:new', async (data) => {
    console.log('New notification received:', data);
    // Play notification sound based on notification type
    // The notification type is nested in data.notification.type based on the server handler
    const notificationType = data?.notification?.type || data?.type || 'default';
    console.log('Playing notification sound for type:', notificationType);

    try {
      await notificationSoundService.playSound(notificationType);
      console.log('Notification sound played successfully');
    } catch (error) {
      console.error('Failed to play notification sound:', error);
    }

    // Fetch updated unread count
    console.log('Fetching updated unread count after new notification...');
    fetchUnreadCount();
  });
  
  useSocketEvent('notification:updated', (data) => {
    console.log('Notification updated:', data);
    // Fetch updated unread count
    fetchUnreadCount();
  });
  
  useSocketEvent('notification:cleared', (data) => {
    console.log('Notifications cleared:', data);
    // Fetch updated unread count
    fetchUnreadCount();
  });
  
  // Filter navigation items based on user permissions
  const filteredNavigationItems = useMemo(() => {
    return navigationItems.map(item => {
      // Handle items with children first
      if (item.children) {
        const filteredChildren = item.children.filter(child =>
          !child.permissionRequired || hasPermission(child.resource, child.action)
        );

        // If at least one child is accessible, show the parent menu
        if (filteredChildren.length > 0) {
          return {
            ...item,
            children: filteredChildren
          };
        }

        // No accessible children, hide the parent
        return null;
      }

      // For items without children, check their own permissions
      if (item.permissionRequired && !hasPermission(item.resource, item.action)) {
        return null;
      }

      return item;
    }).filter(Boolean);
  }, [hasPermission]);

  // Toggle expand/collapse of menu item with children
  const toggleExpand = (itemText) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemText]: !prev[itemText]
    }));
  };
  
  // Check if a navigation item is active - using exact path match
  const isActive = (path) => {
    // For all items, only exact match should be active
    return location.pathname === path;
  };
  
  // Effect to handle mobile drawer
  useEffect(() => {
    if (isMobile) {
      closeSidebar();
      // Don't automatically close mobile drawer on mount
    } else {
      openSidebar();
      closeMobileDrawer(); // Close mobile drawer when switching to desktop
    }
  }, [isMobile, openSidebar, closeSidebar, closeMobileDrawer]);

  // Close mobile drawer when route changes (not when drawer state changes)
  const previousPathname = React.useRef(location.pathname);
  useEffect(() => {
    if (location.pathname !== previousPathname.current) {
      if (isMobile && mobileDrawerOpen) {
        closeMobileDrawer();
      }
      previousPathname.current = location.pathname;
    }
  }, [location.pathname, isMobile, mobileDrawerOpen, closeMobileDrawer]);
  
  // Auto-expand menu items based on active route
  useEffect(() => {
    filteredNavigationItems.forEach(item => {
      if (item.children) {
        // Check if any child or grandchild is active
        const hasActiveChild = item.children.some(child => 
          isActive(child.path) || 
          (child.children && child.children.some(grandchild => isActive(grandchild.path)))
        );
        
        if (hasActiveChild) {
          setExpandedItems(prev => ({
            ...prev,
            [item.text]: true
          }));
        }
      }
    });
  }, [filteredNavigationItems, location.pathname]);
  
  // Handle user menu
  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };
  
  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };
  
  // Handle notifications
  const handleNotificationsOpen = (event) => {
    setNotificationsAnchor(event.currentTarget);
  };
  
  const handleNotificationsClose = () => {
    setNotificationsAnchor(null);
  };
  
  // Handle logout
  const handleLogout = () => {
    handleUserMenuClose();
    logout();
    navigate('/login');
  };
  
  // Handle profile navigation
  const handleProfile = () => {
    handleUserMenuClose();
    navigate('/profile');
  };
  
  // Handle settings navigation
  const handleSettings = () => {
    handleUserMenuClose();
    navigate('/settings');
  };
  
  // Handle theme toggle
  const handleThemeToggle = () => {
    const newTheme = settings.theme === 'light' ? 'dark' : 'light';
    updateSetting('theme', newTheme);
  };
  
  // Render navigation items with nested support
  const renderNavigationItems = (items, depth = 0) => {
    return items.map((item, index) => {
      // Check if this item has children
      const hasChildren = item.children && item.children.length > 0;
      
      // Only consider an item active if its exact path matches AND it doesn't have children
      const directActive = isActive(item.path) && !hasChildren;
      
      // Check if any children are active - only used for auto-expanding parent items
      const childrenActive = hasChildren && item.children.some(child => 
        isActive(child.path) || 
        (child.children && child.children.some(grandchild => isActive(grandchild.path)))
      );
      
      // Only highlight items that are directly active
      const active = directActive;
      
      // Determine active color based on theme mode
      const activeColor = theme.palette.mode === 'dark' 
        ? theme.palette.secondary.main 
        : theme.palette.primary.main;
      
      return (
        <React.Fragment key={item.text}>
          <ListItemAnimation delay={index + depth}>
            <ListItem disablePadding>
              {item.children && item.children.length > 0 ? (
                <Tooltip 
                  title={sidebarCollapsed && !isMobile ? item.text : ''}
                  placement="right"
                  arrow
                  enterDelay={100}
                  leaveDelay={0}
                >
                  <StyledListItemButton
                    active={active}
                    sx={{
                      justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                      px: sidebarCollapsed ? 1 : 3,
                      pl: sidebarCollapsed ? 1 : 3 + (depth * 2),
                    }}
                    onClick={() => !sidebarCollapsed && toggleExpand(item.text)}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: sidebarCollapsed ? 0 : 3,
                        justifyContent: 'center',
                        color: active ? activeColor : 'inherit',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    
                    {(!sidebarCollapsed || isMobile) && (
                      <>
                        <ListItemText 
                          primary={item.text}
                          primaryTypographyProps={{
                            fontFamily: '"Poppins", sans-serif',
                            fontSize: '0.875rem',
                            fontWeight: active ? 600 : 400,
                            color: active ? activeColor : 'inherit',
                          }}
                        />
                        {expandedItems[item.text] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </>
                    )}
                  </StyledListItemButton>
                </Tooltip>
              ) : (
                <Tooltip 
                  title={sidebarCollapsed && !isMobile ? item.text : ''}
                  placement="right"
                  arrow
                  enterDelay={100}
                  leaveDelay={0}
                >
                  <StyledListItemButton
                    component={Link}
                    to={item.path}
                    active={active}
                    sx={{
                      justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                      px: sidebarCollapsed ? 1 : 3,
                      pl: sidebarCollapsed ? 1 : 3 + (depth * 2),
                    }}
                    onClick={() => isMobile && closeMobileDrawer()}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: sidebarCollapsed ? 0 : 3,
                        justifyContent: 'center',
                        color: active ? activeColor : 'inherit',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    
                    {(!sidebarCollapsed || isMobile) && (
                      <ListItemText 
                        primary={item.text}
                        primaryTypographyProps={{
                          fontFamily: '"Poppins", sans-serif',
                          fontSize: '0.875rem',
                          fontWeight: active ? 600 : 400,
                          color: active ? activeColor : 'inherit',
                        }}
                      />
                    )}
                  </StyledListItemButton>
                </Tooltip>
              )}
            </ListItem>
          </ListItemAnimation>
          
          {/* Auto-expand menu when a child is active */}
          {!sidebarCollapsed && item.children && item.children.length > 0 && (
            <Collapse in={expandedItems[item.text] || childrenActive} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {renderNavigationItems(item.children, depth + 1)}
              </List>
            </Collapse>
          )}
        </React.Fragment>
      );
    });
  };
  
  return (
    <Box sx={{ display: 'flex' }}>
      {/* App Bar */}
      <StyledAppBar 
        position="fixed" 
        open={sidebarOpen} 
        collapsed={sidebarCollapsed}
      >
        <Toolbar sx={{ minHeight: 64 }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={isMobile ? openMobileDrawer : toggleSidebar}
            edge="start"
            sx={{
              marginRight: 2,
              ...(!isMobile && sidebarOpen && { display: 'none' }),
            }}
          >
            <MenuIcon />
          </IconButton>
          
          {sidebarOpen && !isMobile && (
            <IconButton 
              onClick={toggleSidebarCollapse}
              color="inherit"
              sx={{ mr: 2 }}
            >
              {sidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          )}
          
          <Typography 
            variant="h6" 
            noWrap 
            component="div" 
            sx={{ 
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              fontSize: { xs: '1rem', md: '1.25rem' },
            }}
          >
            Aghrba Marketing System
          </Typography>
          
          {/* Project Switcher */}
          {location.pathname.includes('/projects/') && (
            <ProjectSwitcher />
          )}
          
          <Box sx={{ flexGrow: 1 }} />
          
          {/* Socket Connection Status */}
          <Tooltip 
            title={isSocketConnected ? 'Connected to server' : 'Disconnected from server'}
            enterDelay={300}
            enterNextDelay={300}
            leaveDelay={0}
            arrow
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
              {isSocketConnected ? (
                <ConnectedIcon 
                  sx={{ 
                    color: theme.palette.success.main, 
                    fontSize: 20
                  }} 
                />
              ) : (
                <DisconnectedIcon 
                  sx={{ 
                    color: theme.palette.error.main, 
                    fontSize: 20,
                    animation: `${blink} 1s infinite`
                  }} 
                />
              )}
            </Box>
          </Tooltip>
          
          {/* Theme toggle */}
          <Tooltip 
            title={`Switch to ${settings.theme === 'light' ? 'dark' : 'light'} mode`}
            enterDelay={300}
            enterNextDelay={300}
            leaveDelay={0}
            arrow
          >
            <MagicalIconButton color="inherit" onClick={handleThemeToggle}>
              {settings.theme === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
            </MagicalIconButton>
          </Tooltip>
          
          {/* Notifications */}
          <Tooltip 
            title="Notifications"
            enterDelay={300}
            enterNextDelay={300}
            leaveDelay={0}
            arrow
          >
            <MagicalIconButton
              color="inherit"
              onClick={handleNotificationsOpen}
            >
              <MagicNotificationBadge badgeContent={unreadCount} color="error">
                <NotificationsIcon />
              </MagicNotificationBadge>
            </MagicalIconButton>
          </Tooltip>
          
          {/* User menu */}
          <Tooltip 
            title="Account settings"
            enterDelay={300}
            enterNextDelay={300}
            leaveDelay={0}
            arrow
          >
            <IconButton
              onClick={handleUserMenuOpen}
              size="small"
              sx={{ ml: 2 }}
              aria-controls={Boolean(userMenuAnchor) ? 'account-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={Boolean(userMenuAnchor) ? 'true' : undefined}
            >
              <MagicalAvatar 
                src={user?.photo}
                alt={user?.firstName}
              >
                {user?.firstName?.charAt(0)}
              </MagicalAvatar>
            </IconButton>
          </Tooltip>
        </Toolbar>
      </StyledAppBar>
      
      {/* Drawer for desktop */}
      <StyledDrawer
        variant="permanent"
        open={sidebarOpen}
        collapsed={sidebarCollapsed}
        sx={{
          display: { xs: 'none', md: 'block' },
        }}
      >
        <DrawerHeader>
          <Logo collapsed={sidebarCollapsed} onClick={() => navigate('/dashboard')}>
            <img src={require('../../assets/images/logo_512.png')} alt="Logo" />
            {!sidebarCollapsed && (
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700,
                  ...gradientTextStyle,
                  fontFamily: '"Playfair Display", serif',
                }}
              >
                Aghrba
              </Typography>
            )}
          </Logo>
        </DrawerHeader>
        
        <Divider sx={{ opacity: 0.1 }} />
        
        {/* Navigation items - wrapped in scrollable box */}
        <Box
          sx={{
            overflowY: 'auto',
            overflowX: 'hidden',
            flex: 1,
            '&::-webkit-scrollbar': {
              width: 8,
            },
            '&::-webkit-scrollbar-track': {
              background: alpha(theme.palette.background.default, 0.1),
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: alpha(theme.palette.primary.main, 0.2),
              borderRadius: 4,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.3),
              },
            },
          }}
        >
          <List>
            {renderNavigationItems(filteredNavigationItems)}
          </List>
          
          {/* Online Users Widget - Super Admin Only */}
          {!sidebarCollapsed && useAuthStore.getState().isSuperAdmin() && (
            <Box sx={{ px: 2, pb: 2, mt: 'auto' }}>
              <OnlineUsersWidget collapsed={false} />
            </Box>
          )}
        </Box>
      </StyledDrawer>
      
      {/* Drawer for mobile */}
      <Drawer
        variant="temporary"
        open={mobileDrawerOpen}
        onClose={closeMobileDrawer}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            width: DRAWER_WIDTH,
            background: theme.aghrba?.gradients?.pageBackground,
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
          },
        }}
      >
        <DrawerHeader>
          <Logo collapsed={false} onClick={() => navigate('/dashboard')}>
            <img src={require('../../assets/images/logo_512.png')} alt="Logo" />
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700,
                ...gradientTextStyle,
                fontFamily: '"Playfair Display", serif',
              }}
            >
              Aghrba
            </Typography>
          </Logo>
          <IconButton onClick={closeMobileDrawer}>
            {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </DrawerHeader>
        
        <Divider sx={{ opacity: 0.1 }} />
        
        {/* Mobile navigation items - wrapped in scrollable box */}
        <Box
          sx={{
            overflowY: 'auto',
            overflowX: 'hidden',
            flex: 1,
            '&::-webkit-scrollbar': {
              width: 8,
            },
            '&::-webkit-scrollbar-track': {
              background: alpha(theme.palette.background.default, 0.1),
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: alpha(theme.palette.primary.main, 0.2),
              borderRadius: 4,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.3),
              },
            },
          }}
        >
          <List>
            {renderNavigationItems(filteredNavigationItems)}
          </List>
          
          {/* Online Users Widget - Super Admin Only */}
          {!sidebarCollapsed && useAuthStore.getState().isSuperAdmin() && (
            <Box sx={{ px: 2, pb: 2, mt: 'auto' }}>
              <OnlineUsersWidget collapsed={false} />
            </Box>
          )}
        </Box>
      </Drawer>
      
      {/* Main content */}
      <Main open={sidebarOpen} collapsed={sidebarCollapsed}>
        <DrawerHeader />
        
        {pageLoading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            height: 'calc(100vh - 64px)' 
          }}>
            <MagicLoading variant="mystical" size="large" message="Loading content..." />
          </Box>
        ) : (
          <Suspense 
            fallback={
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                height: 'calc(100vh - 64px)' 
              }}>
                <MagicLoading variant="mystical" size="large" message="Loading module..." />
              </Box>
            }
          >
            <Outlet />
          </Suspense>
        )}
      </Main>
      
      {/* User menu */}
      <Menu
        anchorEl={userMenuAnchor}
        id="account-menu"
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            maxWidth: 220, // Add this line for consistent width
            // borderRadius: theme.aghrba?.tokens?.borderRadius?.medium,
            background: theme.aghrba?.gradients?.pageBackground,
            ...getCardStyle('magical'),
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem 
          onClick={handleProfile}
          sx={{
            fontFamily: '"Poppins", sans-serif',
            transition: theme.aghrba?.transitions?.standard,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
            },
          }}
        >
          <Avatar>{user?.firstName?.charAt(0)}</Avatar> Profile
        </MenuItem>
        <MenuItem 
          onClick={handleSettings}
          sx={{
            fontFamily: '"Poppins", sans-serif',
            transition: theme.aghrba?.transitions?.standard,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
            },
          }}
        >
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          Settings
        </MenuItem>
        <Divider sx={{ opacity: 0.1 }} />
        <MenuItem 
          onClick={handleLogout}
          sx={{
            fontFamily: '"Poppins", sans-serif',
            transition: theme.aghrba?.transitions?.standard,
            '&:hover': {
              backgroundColor: alpha(theme.palette.error.main, 0.05),
            },
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
      
      {/* Notifications menu */}
      <NotificationsMenu
        anchorEl={notificationsAnchor}
        open={Boolean(notificationsAnchor)}
        onClose={handleNotificationsClose}
      />
      
      {/* Global Task Creation Entry Points */}
      <KeyboardShortcuts />
      <ContextMenu />
      
      {/* Global Task Drawer with URL sync */}
      <GlobalTaskDrawer />
    </Box>
  );
};

export default MagicDashboardLayout;